import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import {
  fetchData,
  postQuery,
  searchfetchQueries,
} from "../../Requirement/services/RequirementServices";
import OfferDetails from "./component/OfferDetails";
import { Typography, TextField } from "@mui/material";
import dayjs from "dayjs";
import Requirements from "./RequirementTab";
import AlertModal from "../../Requirement/components/common/AlerModel";
import TrustScoreModal from "../../../components/common/TrustScoreModal";

const ReqShortlistedOffers = ({ requirementDetails }) => {
  const { setLoading, loading } = useLoading();
  const [offerList, setOfferList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState(null);
  // TODO: Set activeUser dynamically if needed, e.g., useState(requirementDetails?.BuyerEmailID || "");
  const [activeUser] = useState("");
  const [error, setError] = useState(null);
  const [INRToUsd] = useState(0.012);
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    searchProduct: "",
    searchProductGrade: "",
    searchQuantityRange: "",
  });
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    isConfirm: false,
    onConfirm: null,
    onCancel: null,
    autoClose: null,
  });

  // State for Verification Status Modal
  const [trustModal, setTrustModal] = useState(false);

  const trustModel = useCallback(() => {
    setTrustModal(true);
  }, []);

  const quantityOptions = [
    { value: "", label: "Select Range" },
    { value: "50-100", label: "50-100 MT" },
    { value: "100-200", label: "100-200 MT" },
    { value: "200-500", label: "200-500 MT" },
    { value: "Above-500", label: "Above 500 MT" },
  ];

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const loadAllProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductID",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductID",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setAllProductMaster(allProducts);
      } else {
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { Status: "ACTIVE" },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: { Status: "ACTIVE" },
          }),
        ]);

        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const fetchOffers = useCallback(
    async (newRowStart = rowStart) => {
      setLoading(true);
      setError(null);
      try {
        let filterTerms = {
          CategoryType: "requirement",
          BuyerEmailID: activeUser,
          Deleted: 0,
        };

        if (searchValues.requirementNumber) {
          filterTerms.RequirementNumber = searchValues.requirementNumber;
        }

        let productId = "";
        if (searchValues.searchProduct && searchValues.searchProductGrade) {
          const product = allProductMaster.find(
            (p) =>
              p.ProductName === searchValues.searchProduct &&
              p.Grade === searchValues.searchProductGrade
          );
          if (product) {
            productId = product.ProductID;
          } else {
            console.warn(
              `No product found for ProductName: ${searchValues.searchProduct}, Grade: ${searchValues.searchProductGrade}`
            );
          }
        }

        if (productId) {
          filterTerms.ProductID = productId;
        } else if (searchValues.searchProduct) {
          // Fix: Use ProductName instead of product to match backend convention
          filterTerms.product = searchValues.searchProduct;
        }

        // Fix: Append to filterTerms instead of overwriting
        if (searchValues.searchQuantityRange) {
          if (searchValues.searchQuantityRange === "Above-500") {
            filterTerms.QuantityGTE = 500;
          } else {
            const [min, max] = searchValues.searchQuantityRange
              .split("-")
              .map((val) => parseFloat(val));
            filterTerms.QuantityGTE = min;
            filterTerms.QuantityLTE = max;
          }
        }

        const body = {
          target: "listBuyerExtendedRequirementsItems",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms,
        };

        const response = await fetchData(body);
        if (Array.isArray(response.items) || Array.isArray(response)) {
          setOfferList(response.items || response || []);
          setTotalItems(response.total || 0);
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error loading matching offers:", error);
        setError("Failed to load matching offers. Please try again.");
        setOfferList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading, activeUser, searchValues, allProductMaster]
  );

  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProduct: value }));
        if (value) {
          loadProductGrades(value);
        } else {
          setProductGradeList([]);
        }
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
    {
      name: "searchQuantityRange",
      label: "Quantity Range (MT)",
      type: "dropdown",
      options: quantityOptions,
      value: searchValues.searchQuantityRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchQuantityRange: value }));
      },
    },
  ];

  const handlePageChange = useCallback(
    (page) => {
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
    },
    [pageSize]
  );

  // Fix: Reset to page 1 whenever searchValues change (enables live filtering)
  useEffect(() => {
    setRowStart(0);
  }, [searchValues]);

  const currentPage = Math.floor(rowStart / pageSize) + 1;
  const formatPrice = (value) => {
    if (!value || value === "") return "0.00";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  const convertToUSD = (inrValue) => {
    if (!inrValue || inrValue === "") return "0.00";
    const usdValue = parseFloat(inrValue) * INRToUsd;
    return isNaN(usdValue) ? "0.00" : usdValue.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Requirement Number" },
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    {
      id: "Quantity",
      numeric: true,
      label: "Quantity (MT)",
      renderCell: (row) => formatPrice(row.Quantity),
    },
    {
      id: "FOBSalePrice",
      numeric: true,
      label: "FOB Sale Price",
      renderCell: (row) => row.FOBSalePrice,
    },
    { id: "Country", numeric: false, label: "Origin Country" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "INCO", numeric: false, label: "INCO Terms" },
    { id: "OfferType", numeric: false, label: "Offer Type" },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires",
      renderCell: (row) => formatDate(row.Expires),
    },
    {
      id: "KYBStatus",
      numeric: false,
      label: "Seller Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.KYBStatus) {
          case "Approved":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {row.KYBStatus === "Approved" ? "Verified" : "Unverified"}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    loadProductList();
  }, [loadProductList]);

  useEffect(() => {
    loadAllProducts();
  }, [loadAllProducts]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleRowClick = useCallback((row) => {
    setSelectedOffer(row);
    console.log("row", row);
  });

  // Removed handleSearch as it's no longer needed for live filtering

  const handleRemoveOffer = async () => {
    if (!selectedOffer?.BuyerItemID) {
      console.error("No BuyerItemID found for selected offer");
      return;
    }

    setModal({
      show: true,
      type: "warning",
      title: "Are you sure?",
      message:
        "Are you sure you want to remove this offer from your shortlist?",
      isConfirm: true,
      onConfirm: async () => {
        console.log("Confirm clicked for offer:", selectedOffer);
        setLoading(true);
        setError(null); // Clear any existing errors
        try {
          const body = {
            target: "buyerExtendedOfferUpdate",
            BuyerItemExtendedInput: {
              Deleted: 1,
              BuyerItemExtendedID: selectedOffer.BuyerItemExtendedID,
            },
          };

          const result = await postQuery(body, "backendops");

          if (result) {
            // Clear the selected offer
            setSelectedOffer(null);
            // Reload the table
            await fetchOffers();
            console.log("Offer removed successfully");
          }
        } catch (error) {
          console.error("Error removing offer:", error);
          setError("Failed to remove offer. Please try again.");
        } finally {
          setLoading(false);
        }
        setModal((prev) => ({ ...prev, show: false }));
      },
      onCancel: () => setModal((prev) => ({ ...prev, show: false })),
    });
  };

  const Button = ({ label, onClick }) => {
    return (
      <button className="btn-action-re" onClick={onClick}>
        {label}
      </button>
    );
  };

  // Pass the selectedOffer ID to DataTable via selectedRows
  const selectedRows = selectedOffer
    ? [selectedOffer.GPOfferID || selectedOffer.id]
    : [];

  return (
    <div className="matching-offers-container">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        isConfirm={modal.isConfirm}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Shortlisted Offers</div>
        </div>
        {/* Fix: Removed onSearch for live filtering; add back if SearchFilter needs a manual button */}
        <div className="filter4">
          <SearchFilter fields={fields} />
        </div>
      </div>
      {loading && <div className="loading">Loading offers...</div>}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && offerList.length === 0 && (
        <div className="empty-state">No matching offers found.</div>
      )}
      {!loading && offerList.length > 0 && (
        <DataTable
          columns={columns}
          data={offerList}
          total={totalItems}
          title="Matching Offers Information"
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          onRowClick={handleRowClick}
          selectedRows={selectedRows} // Pass selected row ID
        />
      )}
      {selectedOffer && (
        <>
          {/* Offer Details Section */}
          <div className="offer-detail1">
            <div className="header-text-re">
              <div className="head-text">Offer Details</div>
              <div className="button-group">
                <Button label="Verification Status" onClick={trustModel} />
                <Button label="Remove" onClick={handleRemoveOffer} />
              </div>
            </div>
            {
              <OfferDetails
                offer={selectedOffer}
                btnShortlist={false}
                btnSendQuery={false}
                btnRequestQuote={false}
                btnRequestSpecifications={false}
                btnRequestBestPrice={false}
                btnNegotiate={false}
                btnRequestShippingDetails={false}
                btnLogisticsSupport={false}
                btnRaiseQuery={true}
                breakupTab={true}
              />
            }
          </div>

          {/* Requirement Details Section */}
          {selectedOffer && selectedOffer.RequirementsID && (
            <div className="requirement-detail" style={{ marginTop: "20px" }}>
              <div className="header-text-re">
                <div className="head-text">
                  Requirement - {selectedOffer?.RequirementNumber || ""}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Typography variant="body2">Product</Typography>
                  <TextField
                    value={selectedOffer?.ProductName || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                    size="small"
                  />
                </div>
                <div className="col-md-3">
                  <Typography variant="body2">Grade</Typography>
                  <TextField
                    value={selectedOffer?.Grade || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                    size="small"
                  />
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Origin</Typography>
                    <TextField
                      value={selectedOffer.OriginCountry || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Destination</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryLocation
                          ? selectedOffer.DeliveryLocation.split("~")[0]
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Quantity (MT)</Typography>
                    <TextField
                      value={selectedOffer.RqQuantity || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Inco Terms</Typography>
                    <TextField
                      value={selectedOffer.Incoterm || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Payment Terms</Typography>
                    <TextField
                      value={selectedOffer.PaymentTerms || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Packaging</Typography>
                    <TextField
                      value={(() => {
                        const packaging = selectedOffer.ProductPackaging || "";
                        if (packaging.includes("item=")) {
                          const match = packaging.match(/item=([^,}]+)/);
                          return match ? match[1].trim() : packaging;
                        }
                        return packaging;
                      })()}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery Port</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryLocation
                          ? selectedOffer.DeliveryLocation.split("~")[1]
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery (Start)</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryPeriodFrom
                          ? dayjs(selectedOffer.DeliveryPeriodFrom).format(
                              "DD-MM-YYYY"
                            )
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery (End)</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryPeriodTill
                          ? dayjs(selectedOffer.DeliveryPeriodTill).format(
                              "DD-MM-YYYY"
                            )
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Valid Until</Typography>
                    <TextField
                      value={
                        selectedOffer.ValidUntil
                          ? dayjs(selectedOffer.ValidUntil).format("DD-MM-YYYY")
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {/* Verification Status Modal */}
      {trustModal && (
        <TrustScoreModal
          onClose={() => setTrustModal(false)}
          userEmail={selectedOffer?.SellerEmailID}
        />
      )}
    </div>
  );
};

export default ReqShortlistedOffers;
